// s126 — jurisdiction-neutral "professional help" recommendations on executor tasks.
// Stored as a jsonb array on `task_templates.professional_help`; rendered as a small
// recommendation pill per role on the task row + a line in the drawer's "Who can help"
// zone. Deliberately NOT "CPA" (US-specific) — "accountant" reads across jurisdictions.
// Array, not enum: combinations are just multiple entries and future roles need no
// schema change. Coexists with Class-D matched-contact bindings (pill = the generic
// recommendation; the card = the actual person the planner recorded).

export const PROFESSIONAL_HELP_ROLES = ["lawyer", "accountant", "financial_advisor"] as const;
export type ProfessionalHelpRole = (typeof PROFESSIONAL_HELP_ROLES)[number];

/** Pill label per role (task row).
 *
 * 🔴 "REVIEW recommended", not "recommended" (s181 ruling). The two phrasings say different
 * things: "Lawyer recommended" reads as *hire a lawyer to do this task*; "Lawyer review
 * recommended" reads as *have a lawyer check this before it is filed*. The second is what the
 * product means, and it is the more defensible position on the unauthorized-practice-of-law
 * question. The whole set moves together — a member left on the old wording reintroduces the
 * ambiguity for that role alone. Do not reword. */
export const PROFESSIONAL_HELP_LABEL: Record<ProfessionalHelpRole, string> = {
  lawyer: "Lawyer review recommended",
  accountant: "Accountant review recommended",
  financial_advisor: "Financial advisor review recommended",
};

/** Short noun per role (drawer "Who can help" recommendation line). */
export const PROFESSIONAL_HELP_SHORT: Record<ProfessionalHelpRole, string> = {
  lawyer: "Lawyer",
  accountant: "Accountant",
  financial_advisor: "Financial advisor",
};

export function isProfessionalHelpRole(v: string): v is ProfessionalHelpRole {
  return (PROFESSIONAL_HELP_ROLES as readonly string[]).includes(v);
}
